import React from 'react'
import { X } from 'lucide-react'
import { Input, Label, Modal } from '@/shared/ui'
import { useCommunityDraftAttachments } from '@/modules/comunidade/hooks/useCommunityDraftAttachments'
import { extractCommunityMessagePlainText } from '@/modules/comunidade/utils/communityMessageContent'
import type { VisionPostAttachment } from '../services/visionPostAttachmentsService'
import { PostAttachmentsEditor, PostComposer } from '@/modules/post-shared'
import { VisionOpportunityLinkField } from './VisionOpportunityLinkField'
import { normalizeLinkedOpportunityId } from '../utils/normalizeLinkedOpportunityId'
import { normalizeLinkedTimelineRootId } from '../utils/normalizeLinkedTimelineRootId'
import { VisionLinkedOpportunityNotice } from './VisionLinkedOpportunityNotice'
import { VisionTimelineLinkField } from './VisionTimelineLinkField'

export type NewVisionModalSubmitParams = {
  title: string
  html: string
  newAttachments: File[]
  removeAttachmentIds: number[]
  linkedOpportunityId: number | null
  linkedTimelineRootId: number | null
}

type NewVisionModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (params: NewVisionModalSubmitParams) => void | Promise<void>
  visionId?: number
  initialTitle?: string
  initialHtml?: string
  initialAttachments?: VisionPostAttachment[]
  initialLinkedOpportunityId?: number | null
  initialLinkedTimelineRootId?: number | null
  mode?: 'create' | 'edit'
}

export function NewVisionModal({
  open,
  onOpenChange,
  onSubmit,
  visionId,
  initialTitle,
  initialHtml,
  initialAttachments,
  initialLinkedOpportunityId,
  initialLinkedTimelineRootId,
  mode = 'create',
}: NewVisionModalProps) {
  const [title, setTitle] = React.useState(initialTitle || '')
  const [html, setHtml] = React.useState(initialHtml || '')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [linkEnabled, setLinkEnabled] = React.useState(false)
  const [linkedOpportunityId, setLinkedOpportunityId] = React.useState<number | null>(null)
  const [timelineLinkEnabled, setTimelineLinkEnabled] = React.useState(false)
  const [linkedTimelineRootId, setLinkedTimelineRootId] = React.useState<number | null>(null)
  const [existingAttachments, setExistingAttachments] = React.useState<VisionPostAttachment[]>(
    Array.isArray(initialAttachments) ? initialAttachments : [],
  )
  const [removeAttachmentIds, setRemoveAttachmentIds] = React.useState<number[]>([])

  const {
    attachments: draftAttachments,
    addFiles: addDraftFiles,
    removeAttachment: removeDraftAttachment,
    clear: clearDraftAttachments,
    error: attachmentError,
  } = useCommunityDraftAttachments()

  const isEdit = mode === 'edit'

  const hasContent = React.useMemo(() => {
    const text = extractCommunityMessagePlainText(html || '')
    return !!String(text || '').trim()
  }, [html])

  const canSubmit = React.useMemo(() => {
    const hasTitle = !!title.trim()
    const hasOpportunitySelection = !linkEnabled || linkedOpportunityId !== null
    const hasTimelineSelection = !timelineLinkEnabled || linkedTimelineRootId !== null
    return hasTitle && hasContent && hasOpportunitySelection && hasTimelineSelection
  }, [hasContent, linkEnabled, linkedOpportunityId, timelineLinkEnabled, linkedTimelineRootId, title])

  const reset = React.useCallback(() => {
    setTitle(initialTitle || '')
    setHtml(initialHtml || '')
    setError(null)
    const initialLink = normalizeLinkedOpportunityId(initialLinkedOpportunityId)
    const initialTimeline = normalizeLinkedTimelineRootId(initialLinkedTimelineRootId)
    const useTimeline = initialTimeline !== null && initialLink === null
    setLinkEnabled(initialLink !== null)
    setLinkedOpportunityId(initialLink)
    setTimelineLinkEnabled(useTimeline)
    setLinkedTimelineRootId(useTimeline ? initialTimeline : null)
    setExistingAttachments(Array.isArray(initialAttachments) ? initialAttachments : [])
    setRemoveAttachmentIds([])
    clearDraftAttachments()
  }, [
    clearDraftAttachments,
    initialAttachments,
    initialHtml,
    initialLinkedOpportunityId,
    initialLinkedTimelineRootId,
    initialTitle,
  ])

  React.useEffect(() => {
    if (!open) return
    reset()
  }, [open, reset])

  const handleClose = React.useCallback(() => {
    onOpenChange(false)
    reset()
  }, [onOpenChange, reset])

  const handleRemoveExisting = React.useCallback((id: number) => {
    setExistingAttachments((prev) => prev.filter((att) => att.id !== id))
    setRemoveAttachmentIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])

  const handleOpportunityEnabledChange = React.useCallback((next: boolean) => {
    setLinkEnabled(next)
    if (next) {
      setTimelineLinkEnabled(false)
      setLinkedTimelineRootId(null)
    }
  }, [])

  const handleTimelineEnabledChange = React.useCallback((next: boolean) => {
    setTimelineLinkEnabled(next)
    if (next) {
      setLinkEnabled(false)
      setLinkedOpportunityId(null)
    }
  }, [])

  const handleSubmit = React.useCallback(async () => {
    const trimmedTitle = title.trim()
    const trimmedHtml = String(html || '').trim()
    const newFiles = draftAttachments.map((a) => a.file).filter(Boolean)

    if (!trimmedTitle) {
      setError('Preencha o titulo antes de enviar.')
      return
    }

    if (!hasContent) {
      setError('Preencha a descricao antes de enviar.')
      return
    }

    if (linkEnabled && linkedOpportunityId === null) {
      setError('Selecione uma oportunidade confirmada ou desative o vinculo.')
      return
    }

    if (timelineLinkEnabled && linkedTimelineRootId === null) {
      setError('Selecione um post publicado ou desative o vinculo da timeline.')
      return
    }

    setSaving(true)
    setError(null)
    try {
      if (onSubmit) {
        await onSubmit({
          title: trimmedTitle,
          html: trimmedHtml,
          newAttachments: newFiles,
          removeAttachmentIds,
          linkedOpportunityId: linkEnabled ? linkedOpportunityId : null,
          linkedTimelineRootId: timelineLinkEnabled ? linkedTimelineRootId : null,
        })
      }
      onOpenChange(false)
      reset()
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }, [
    draftAttachments,
    hasContent,
    html,
    linkEnabled,
    linkedOpportunityId,
    timelineLinkEnabled,
    linkedTimelineRootId,
    onOpenChange,
    onSubmit,
    removeAttachmentIds,
    reset,
    title,
  ])

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          handleClose()
        } else {
          onOpenChange(true)
        }
      }}
      className="w-full max-w-2xl p-6"
    >
      <div className="space-y-4">
        <div className="relative pr-10">
          <button
            type="button"
            aria-label="Fechar"
            disabled={saving}
            onClick={handleClose}
            className="absolute right-0 top-0 rounded p-1 text-foreground/70 hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold">
            {isEdit ? 'Editar visao' : 'Nova visao'}
          </h2>
          <p className="text-xs text-foreground/60">
            {isEdit
              ? 'Altere o titulo, o conteudo e os anexos da visao.'
              : 'Crie uma nova visao para os membros autorizados.'}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="vision-title">Titulo</Label>
          <Input
            id="vision-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex.: Zona de interesse para BTC no curto prazo"
          />
        </div>

        <VisionOpportunityLinkField
          enabled={linkEnabled}
          selectedOpportunityId={linkedOpportunityId}
          onEnabledChange={handleOpportunityEnabledChange}
          onSelectedOpportunityIdChange={setLinkedOpportunityId}
          disabled={saving}
        />

        <VisionTimelineLinkField
          enabled={timelineLinkEnabled}
          selectedRootId={linkedTimelineRootId}
          onEnabledChange={handleTimelineEnabledChange}
          onSelectedRootIdChange={setLinkedTimelineRootId}
          disabled={saving}
          excludeVisionId={visionId ?? null}
        />

        <VisionLinkedOpportunityNotice
          opportunityId={linkedOpportunityId}
          excludeVisionId={visionId ?? null}
          visible={linkEnabled && linkedOpportunityId != null}
        />

        <div className="space-y-2">
          <Label>Descricao</Label>

          {attachmentError && <div className="text-[11px] text-red-400">{attachmentError}</div>}

          <PostAttachmentsEditor
            existingAttachments={existingAttachments}
            onRemoveExisting={handleRemoveExisting}
            draftAttachments={draftAttachments}
            onRemoveDraft={removeDraftAttachment}
          />

          <PostComposer
            value={html}
            onChange={setHtml}
            placeholder="Digite sua visao aqui..."
            onAddFiles={addDraftFiles}
            disabled={saving}
            onSubmit={handleSubmit}
            submitDisabled={saving || !canSubmit}
          />

          <p className="text-xs text-foreground/60">
            Use o editor para formatar o texto, anexar arquivos/imagens, inserir emojis e mencionar usuarios com @.
          </p>
        </div>

        {error && <div className="text-xs text-destructive">{error}</div>}
      </div>
    </Modal>
  )
}
